import { AfterViewInit, Component, ChangeDetectionStrategy, DestroyRef, ElementRef, OnDestroy, OnInit, OnChanges, Renderer2, SimpleChanges, inject, input, viewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { IStream } from "@auvious/rtc";
import { TileComponent } from "../tile/tile.component";
import { merge } from "rxjs";
import { filter } from "rxjs/operators";
import { AudioVisualizerEffect } from "@auvious/media-tools";
import { debugError, ThemeService } from "../../../core-ui/services";
import { ConferenceService } from "../../services";
import { LocalMediaService } from "../../services/local.media.service";
import { ThemeParam } from "../../../core-ui/models";

@Component({
  selector: "app-audio-bubble",
  templateUrl: "./audio-bubble.component.html",
  styleUrls: ["./audio-bubble.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AudioBubbleComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  private renderer = inject(Renderer2);
  private host = inject<ElementRef<HTMLDivElement>>(ElementRef);
  private conference = inject(ConferenceService);
  private local = inject(LocalMediaService);
  private theme = inject(ThemeService);
  private tile = inject(TileComponent, { optional: true });
  private destroyRef = inject(DestroyRef);

  readonly streamId = input<string>(undefined);

  private colorPrimary = "#4ca2ff";
  private colorSecondary = "#ff00ff";

  private visualizer: AudioVisualizerEffect | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private resizeRaf = 0;

  readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("canvas");

  ngOnInit(): void {
    this.theme.themeChanged$().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((th) => {
      this.colorPrimary = th[ThemeParam.AI_AGENT_PRIMARY_COLOR];
      this.colorSecondary = th[ThemeParam.AI_AGENT_SECONDARY_COLOR];
    });
    this.tile?.viewSizeChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value?.width > 1 && value?.height > 1) {
        this.renderer.setStyle(
          this.host.nativeElement,
          "width",
          `${value.width}px`
        );
        this.renderer.setStyle(
          this.host.nativeElement,
          "height",
          `${value.height}px`
        );
      }
      this.ensureVisualizer();
      this.refreshStreamBinding();
      this.syncVisualizerCanvas();
    });
  }
  ngAfterViewInit(): void {
    this.ensureVisualizer();
    this.refreshStreamBinding();
    this.registerConferenceListeners();
    this.resizeCanvas();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["streamId"] && !changes["streamId"].firstChange) {
      this.refreshStreamBinding();
    }
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.resizeRaf);
    this.resizeObserver?.disconnect();
    this.visualizer?.reset();
    this.visualizer?.dispose();
  }

  private ensureVisualizer() {
    const canvasRef = this.canvasRef();
    const canvasReady = !!canvasRef?.nativeElement;

    if (this.visualizer || !canvasReady) {
      return;
    }

    try {
      this.visualizer = new AudioVisualizerEffect({
        canvas: canvasRef.nativeElement,
        colorPrimary: this.colorPrimary,
        colorSecondary: this.colorSecondary,
      });
      // ensure initial sizing with current dimensions
      this.syncVisualizerCanvas();
    } catch (ex) {
      debugError(ex);
      this.visualizer = null;
    }
  }

  /**
   * Adjusts the canvas internal resolution to match display size.
   * This is critical for crisp rendering on high DPI screens.
   */
  private resizeCanvas() {
    const canvasRef = this.canvasRef();
    if (!canvasRef) return;

    const canvas = canvasRef.nativeElement;
    const dpr = 1; // window.devicePixelRatio || 1;

    const wrapper = canvas.parentElement as HTMLElement | null;
    const rect =
      wrapper?.getBoundingClientRect() ||
      this.host.nativeElement.getBoundingClientRect() ||
      (canvas as HTMLElement).getBoundingClientRect();

    const width = Math.floor(rect?.width || 0);
    const height = Math.floor(rect?.height || 0);

    if (width <= 1 || height <= 1) return;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    this.syncVisualizerCanvas();
  }

  private refreshStreamBinding() {
    this.ensureVisualizer();

    if (!this.visualizer || !this.streamId()) {
      this.visualizer?.reset();
      return;
    }

    const mediaStream = this.getStream()?.mediaStream;

    if (!mediaStream || !mediaStream.getAudioTracks().length) {
      this.visualizer?.reset();
      return;
    }

    try {
      this.visualizer.setInput(mediaStream);
      // this.resizeCanvasSoon();
      this.syncVisualizerCanvas();
    } catch (ex) {
      debugError(ex);
      this.visualizer.reset();
    }
  }

  private getStream(): IStream | null {
    try {
      const streamId = this.streamId();
      return streamId ? this.conference.getStream(streamId) : null;
    } catch (ex) {
      debugError(ex);
      return null;
    }
  }

  private registerConferenceListeners() {
    this.conference.streamStateChanged$
      .pipe(filter((event) => event.streamId === this.streamId()), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshStreamBinding());

    merge(this.conference.streamMutedChange$, this.local.streamMutedChange$)
      .pipe(filter((event) => event.stream?.id === this.streamId()), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshStreamBinding());
  }

  private syncVisualizerCanvas() {
    const resizeFn = (this.visualizer as any)?.resizeCanvas;
    if (typeof resizeFn === "function") {
      try {
        resizeFn.call(this.visualizer);
      } catch (ex) {
        debugError(ex);
      }
    }
  }
}
